import { useCallback, useEffect, useMemo, useRef } from "react";
import { HostWindowCommService } from "../../Services/IframeHostService/HostWindowCommService";
import { OEvents } from "../../Services/IframeHostService/models";
import { useAppSelector } from "../../Store";
import { allMessagesStore } from "../../Store/Slices/socket/AllMessagesSlice";
import { botInfoStore } from "../../Store/Slices/socket/BotInfoSlice";

// This hook is what makes PiP open AUTOMATICALLY on tab-switch, the way
// Google Meet's own tab does — the whole point of the feature, not
// incidental. Chrome only auto-invokes the mediaSession
// "enterpictureinpicture" handler (i.e. opens PiP with no click at all,
// purely from the tab being hidden) for a page it considers eligible for
// automatic PiP, and that eligibility is Chrome's own, undocumented-as-a-
// stable-API heuristic: in practice it requires an active audio/video
// MediaStream plus a registered mediaSession handler, the same signal Meet
// itself relies on. There is no separate "just let me auto-open PiP"
// permission or API — satisfying this eligibility gate genuinely means
// holding a live getUserMedia() stream for the session, exactly like a
// real video-call tab does. Removing the mic stream removes automatic
// PiP entirely, leaving only a manual "click to pop out" trigger — that
// is a real, deliberate product trade-off (mic-permission prompt + a live
// mic indicator vs. automatic hand-off), not a bug to be silently fixed.
// If that trade-off ever needs to change, it should be a product decision
// (e.g. gate this behind a bot-config flag, or only piggyback on a
// session that already has voice input active) rather than an incidental
// cleanup.
//
// documentPictureInPicture.requestWindow() requires a fresh, direct user
// click from a TOP-LEVEL browsing context — confirmed via real-device
// testing that it throws NotAllowedError ("only allowed from a top-level
// browsing context") even when called from inside Chrome's own auto-invoked
// `enterpictureinpicture` MediaSession handler, because the widget always
// runs inside `<iframe class="geta-iframe-wrapper">` on the host page. So
// this hook's job is only to satisfy Chrome's eligibility gate (the live
// getUserMedia stream + mediaSession handler described above) and, once
// Chrome auto-invokes the handler, ask the loader (geta-tracking-scripts,
// which IS top-level) to actually open the window — same as the
// CHATBOT_OPEN_PIP request the loader's GetAChatBotFrame.openPipWindow()
// already handles.
//
// The widget's existing voice input feature does NOT satisfy the
// eligibility gate on its own (it uses the Web Speech API, which captures
// audio through an internal Chrome pathway that never produces a
// page-visible MediaStream) — so this opens its own, separate, dedicated
// audio stream purely to unlock the gate.
//
// enableAutoPipForThisSession takes the bot's pip_auto_open_enabled config
// flag as a parameter (see AppInputHandler.tsx) rather than reading it
// itself. Code review flagged the unconditional mic-permission prompt as
// a High-severity concern (it happens on every customer's own domain, and
// anyone who allows it gets a live mic-recording indicator on their tab
// for the whole session) and asked for a default-false opt-in flag. This
// currently defaults TRUE instead (on for every bot out of the box), so
// the auto-PiP hand-off works immediately without an admin-panel setting
// existing yet — a deliberate, temporary product choice, not an oversight
// of that review feedback. A bot can still opt out explicitly by setting
// pip_auto_open_enabled to false.
const isMediaSessionSupported = () =>
  typeof navigator !== "undefined" &&
  "mediaSession" in navigator &&
  "mediaDevices" in navigator;

const useVoiceGatedAutoPip = () => {
  const enabledRef = useRef(false);
  const streamRef = useRef<MediaStream | null>(null);
  const commService = useMemo(() => HostWindowCommService.getInstance(), []);

  // Only float the widget when there's actually something pending to see —
  // same gating condition as usePendingChatNotification.tsx (tab hidden,
  // chat was open, and a response is pending/unread). Refs so the
  // mediaSession handler (registered once, per session) always reads the
  // latest values without needing to re-register itself.
  const { typingStatus, isNextNodeAvailable } = useAppSelector(allMessagesStore);
  const { chatOpened } = useAppSelector(botInfoStore);
  const typingStatusRef = useRef(typingStatus);
  const chatOpenedRef = useRef(chatOpened);
  typingStatusRef.current = typingStatus;
  chatOpenedRef.current = chatOpened;

  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  // Also release the mic the moment the conversation itself ends (rather
  // than only on unmount) — the eligibility gate no longer needs to be
  // held open past that point, and a live mic indicator outliving the
  // chat has no purpose.
  useEffect(() => {
    if (isNextNodeAvailable === false) {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, [isNextNodeAvailable]);

  // Tied to a real user gesture (first message send), matching the pattern
  // already used elsewhere in this widget for permission requests that must
  // originate from direct user intent.
  const enableAutoPipForThisSession = useCallback(
    (autoOpenEnabled: boolean) => {
      if (!autoOpenEnabled || !isMediaSessionSupported() || enabledRef.current) {
        return;
      }
      enabledRef.current = true;

      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          // Never stop these tracks for the life of the widget — an inactive
          // stream immediately makes the page ineligible for auto-PiP again.
          streamRef.current = stream;

          navigator.mediaSession.setActionHandler("enterpictureinpicture", () => {
            if (!chatOpenedRef.current || !typingStatusRef.current) {
              return;
            }
            commService.postMessage({
              event_type: OEvents.CHATBOT_OPEN_PIP,
              data: {},
            });
          });
        })
        .catch(() => {
          // User denied the microphone prompt (or no mic device exists) — this
          // feature simply never activates for them. No error shown; the
          // Notification-based pending-chat feature still applies normally.
        });
    },
    [commService]
  );

  return { enableAutoPipForThisSession };
};

export default useVoiceGatedAutoPip;
